import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, ImageIcon, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Fetches /api/files/{fileId}/download (with auth) and renders the result.
 * Supports images, PDF, and other types via download link.
 */
export function FilePreview({ fileId, compact = false }) {
  const [meta, setMeta] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!fileId) {
      setError("ID berkas kosong.");
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
        if (cancelled) return;
        const ct = res.headers["content-type"] || res.data.type || "application/octet-stream";
        createdUrl = URL.createObjectURL(res.data);
        setBlobUrl(createdUrl);
        setMeta({ contentType: ct });
      } catch (e) {
        if (!cancelled) setError("Gagal memuat bukti.");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fileId]);

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2">
        {error}
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div
        className={`rounded-md border border-slate-200 bg-slate-100 animate-pulse ${
          compact ? "h-20 w-20" : "h-32 w-full"
        }`}
      />
    );
  }

  const ct = meta?.contentType || "";
  const isImage = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";

  if (isImage) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`group relative block overflow-hidden rounded-md border border-slate-200 bg-slate-100 ${
            compact ? "h-20 w-20" : "h-32 w-full"
          }`}
          data-testid={`bukti-preview-${fileId}`}
        >
          <img src={blobUrl} alt="Bukti" className="h-full w-full object-cover" />
          <span className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors" />
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Bukti Pendukung
              </DialogTitle>
            </DialogHeader>
            <div className="bg-slate-50 rounded-md overflow-hidden">
              <img src={blobUrl} alt="Bukti" className="w-full max-h-[75vh] object-contain" />
            </div>
            <a
              href={blobUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> Buka di tab baru
            </a>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // PDF or other
  return (
    <a
      href={blobUrl}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold hover:bg-slate-50 ${
        compact ? "h-20 w-20 flex-col justify-center text-center" : ""
      }`}
      data-testid={`bukti-link-${fileId}`}
    >
      <FileText className="h-4 w-4 text-slate-700" />
      {isPdf ? "PDF" : "Berkas"}
      <ExternalLink className="h-3 w-3 ml-auto opacity-60" />
    </a>
  );
}
